<!-- src/views/LoginPage.vue -->
<template>
  <div class="login-container">
    <!-- 动态背景画布 -->
    <canvas id="dynamic-bg-canvas"></canvas>
    <div class="bg-gradient-overlay"></div>
    
    <div class="login-box">
      <h2>N.Ships Co., Limited (China)</h2>
      <p class="title-desc">Operation Management System</p>

      <div v-if="loginError" class="login-error show">
        {{ loginError }}
      </div>

      <div class="form-item">
        <label>Department</label>
        <select v-model="selectedDept" class="form-select">
          <option value="">Select Department</option>
          <option value="management">Management Department</option>
          <option value="finance">Finance Department</option>
          <option value="market">Marketing Department</option>
          <option value="operation">Operations Department</option>
        </select>
      </div>

      <div class="form-item">
        <label>Username</label>
        <input 
          type="text" 
          v-model="username" 
          placeholder="Enter username"
          @keypress.enter="handleLogin"
          class="form-input"
        />
      </div>

      <div class="form-item">
        <label>Password</label>
        <input 
          type="password" 
          v-model="password" 
          placeholder="Enter password"
          @keypress.enter="handleLogin"
          class="form-input"
        />
      </div>

      <button 
        class="login-btn" 
        @click="handleLogin"
        :disabled="isLoading"
      >
        <span v-if="!isLoading">Login →</span>
        <span v-else>Logging in...</span>
      </button>

      <!-- 版权信息 + 备案号 -->
      <div class="copyright">
        <div>© 2020-2026 N.Ships Co., Limited (China)</div>
        <div>Maritime Solutions in China Ports</div>
        <div class="beian-wrapper">
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=32060202002982" rel="noreferrer" target="_blank" class="ga-link">
            <img src="/beian.png" alt="公安备案图标" class="ga-icon">
            苏公网安备32060202002982号
          </a>
          <span class="beian-separator">&nbsp;</span>
          <a href="https://beian.miit.gov.cn/" target="_blank">苏ICP备2021052578号</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginPage',
  data() {
    return {
      selectedDept: '',
      username: '',
      password: '',
      isLoading: false,
      loginError: '',
      // API 基础地址（从环境变量读取，默认为 /api）
     apiBaseUrl: '/api',
      // 粒子背景相关
      canvas: null,
      ctx: null,
      width: 0,
      height: 0,
      particles: [],
      waveOffset: 0,
      animationId: null
    }
  },
  mounted() {
    // 初始化粒子背景
    this.initCanvas()
    this.initParticles()
    this.drawBackground()
    
    // 回填用户名（如果有）
    const savedUser = localStorage.getItem('username')
    if (savedUser) {
      this.username = savedUser
    }
    
    // 窗口大小变化时重新调整
    window.addEventListener('resize', this.handleResize)

    // 打印当前使用的 API 地址（调试用）
    console.log('🔗 API 地址:', this.apiBaseUrl)
  },
  beforeUnmount() {
    // 清理动画和事件监听
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // ========== 登录逻辑 ==========
    async handleLogin() {
      // 1️⃣ 验证表单
      if (!this.selectedDept || !this.username || !this.password) {
        this.loginError = 'Please fill in all fields'
        return
      }

      this.isLoading = true
      this.loginError = ''

      try {
        // 2️⃣ 调用后端 API
        const response = await fetch(`${this.apiBaseUrl}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: this.username.trim(),
            password: this.password.trim(),
            department: this.selectedDept
          })
        })

        const result = await response.json()

        // 3️⃣ 检查业务逻辑是否成功
        if (!result.success) {
          this.loginError = result.message || 'Login failed'
          this.isLoading = false
          return
        }

        // ============================================================
        // ✅ 4️⃣ 登录成功 - 保存信息并跳转
        // ============================================================
        console.log('✅ 登录成功', {
          username: result.data.user.username,
          department: result.data.user.department,
          role: result.data.user.role
        })

        // 保存用户信息到 localStorage
        localStorage.setItem('token', result.data.token)
        localStorage.setItem('username', result.data.user.username)
        localStorage.setItem('userRole', result.data.user.department)
        localStorage.setItem('realName', result.data.user.realName || '')
        localStorage.setItem('userId', String(result.data.user.id))

        // 部门 → 主页 映射表
        const mainMap = {
          management: '/management/main',
          finance: '/finance/main',
          market: '/market/main',
          operation: '/operation/main'
        }
        
        // 根据部门跳转，如果部门不匹配则跳转到登录页（安全兜底）
        const redirectPath = mainMap[result.data.user.department] || '/login'
        console.log('📍 跳转到:', redirectPath)
        
        this.$router.push(redirectPath)

      } catch (error) {
        // ============================================================
        // ❌ 5️⃣ 登录失败 - 停留在登录页，不跳转
        // ============================================================
        console.error('❌ 登录失败:', error)
        
        // 根据错误类型显示更友好的提示
        if (error.message && error.message.includes('Failed to fetch')) {
          this.loginError = '⚠️ 无法连接到服务器，请检查网络或稍后重试'
        } else if (error.message && error.message.includes('401')) {
          this.loginError = '❌ 用户名或密码错误，请重新输入'
        } else {
          this.loginError = '❌ 登录失败，请稍后重试'
        }
        
        this.password = ''
        // ✅ 关键修复：删除 this.$router.push(redirectPath)
        // 登录失败时停留在当前登录页

      } finally {
        this.isLoading = false
      }
    },

    // ========== 粒子背景逻辑（保持不变） ==========
    initCanvas() {
      this.canvas = document.getElementById('dynamic-bg-canvas')
      if (!this.canvas) return
      
      this.ctx = this.canvas.getContext('2d')
      this.resizeCanvas()
    },

    resizeCanvas() {
      if (!this.canvas) return
      this.width = window.innerWidth
      this.height = window.innerHeight
      this.canvas.width = this.width
      this.canvas.height = this.height
    },

    initParticles() {
      const PARTICLE_COUNT = 180
      this.particles = []
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        this.particles.push({
          x: Math.random(),
          y: Math.random(),
          radius: 2 + Math.random() * 4,
          speedX: (Math.random() - 0.5) * 0.002,
          speedY: (Math.random() - 0.5) * 0.001,
          alpha: 0.3 + Math.random() * 0.5
        })
      }
    },

    drawBackground() {
      if (!this.ctx || !this.canvas) return
      
      const ctx = this.ctx
      const width = this.width
      const height = this.height
      const particles = this.particles
      
      // 绘制渐变背景
      const grad = ctx.createLinearGradient(0, 0, width, height)
      grad.addColorStop(0, '#021022')
      grad.addColorStop(0.5, '#0a2f44')
      grad.addColorStop(1, '#01141f')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, width, height)
      
      // 绘制波浪
      ctx.save()
      ctx.globalAlpha = 0.45
      for (let i = 0; i < 3; i++) {
        ctx.beginPath()
        const amp = 40 + i * 15
        const freq = 0.005 + i * 0.002
        const yBase = height * (0.5 + i * 0.15)
        for (let x = 0; x <= width; x += 20) {
          const y = yBase + Math.sin(x * freq + this.waveOffset + i * 1.2) * amp + 
                    Math.cos(x * 0.003 + this.waveOffset * 1.3) * 15
          if (x === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.strokeStyle = i === 0 ? '#3b82f6' : (i === 1 ? '#60a5fa' : '#38bdf8')
        ctx.lineWidth = 2.5
        ctx.stroke()
      }
      ctx.restore()
      
      // 绘制粒子
      ctx.save()
      for (let p = 0; p < particles.length; p++) {
        const pt = particles[p]
        ctx.beginPath()
        ctx.arc(pt.x * width, pt.y * height, pt.radius, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(180, 220, 255, ' + (pt.alpha * 0.7) + ')'
        ctx.fill()
        pt.x += pt.speedX
        pt.y += pt.speedY
        if (pt.x < 0) pt.x = 1
        if (pt.x > 1) pt.x = 0
        if (pt.y < 0) pt.y = 1
        if (pt.y > 1) pt.y = 0
      }
      ctx.restore()
      
      this.waveOffset += 0.012
      
      // 继续动画循环
      this.animationId = requestAnimationFrame(this.drawBackground)
    },

    handleResize() {
      this.resizeCanvas()
      this.initParticles()
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  backdrop-filter: blur(2px);
  background: #0a0f1e;
}

#dynamic-bg-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
  display: block;
}

.bg-gradient-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(10, 20, 40, 0.4) 0%, rgba(2, 8, 20, 0.85) 100%);
  z-index: -1;
}

.login-box {
  width: 480px;
  max-width: 92%;
  background: rgba(15, 25, 45, 0.65);
  backdrop-filter: blur(16px);
  border-radius: 32px;
  padding: 48px 40px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08);
  transition: transform 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1;
}

.login-box:hover {
  transform: translateY(-5px);
}

.login-box h2 {
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(135deg, #FFFFFF 0%, #B0D4FF 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 12px;
  white-space: nowrap;
  overflow-x: auto;
}

.title-desc {
  text-align: center;
  color: rgba(220, 240, 255, 0.8);
  font-size: 14px;
  margin-bottom: 40px;
}

.form-item {
  margin-bottom: 28px;
}

.form-item label {
  display: block;
  margin-bottom: 10px;
  color: #E0F0FF;
  font-weight: 500;
  font-size: 14px;
}

.form-item .form-input,
.form-item .form-select {
  width: 100%;
  height: 52px;
  padding: 0 18px;
  background: rgba(10, 20, 35, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  font-size: 15px;
  color: white;
  outline: none;
  backdrop-filter: blur(4px);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-item .form-select option {
  background: #0a1428;
  color: white;
}

.form-item .form-input:focus,
.form-item .form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  background: rgba(10, 20, 35, 0.8);
}

.form-item .form-input::placeholder {
  color: rgba(180, 200, 230, 0.5);
}

.login-btn {
  width: 100%;
  height: 54px;
  background: linear-gradient(105deg, #1e4b8f, #0f2b55);
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;
}

.login-btn:hover:not(:disabled) {
  background: linear-gradient(105deg, #2f66b0, #1b3b6e);
  transform: scale(1.02);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.login-error {
  color: #dc3545;
  font-size: 13px;
  text-align: center;
  margin-bottom: 15px;
  min-height: 20px;
  display: none;
}

.login-error.show {
  display: block;
}

/* ============================================================
   版权信息 + 备案号
   ============================================================ */
.copyright {
  text-align: center;
  margin-top: 42px;
  color: rgba(180, 200, 230, 0.7);
  font-size: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 24px;
  line-height: 1.8;
}

.copyright div {
  line-height: 1.8;
}

/* 备案号容器 - 使用 flex 确保对齐 */
.beian-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  margin-top: 2px;
}

/* 公安备案链接样式 */
.ga-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  color: rgba(180, 200, 230, 0.7);
  transition: color 0.3s ease;
}

.ga-link:hover {
  color: #60a5fa;
}

/* 公安备案图标样式 */
.ga-icon {
  height: 16px;
  width: auto;
  vertical-align: middle;
  display: inline-block;
  flex-shrink: 0;
}

/* 分隔符 */
.beian-separator {
  display: inline-block;
  width: 6px;
}

/* ICP备案链接样式 */
.beian-wrapper a:last-child {
  text-decoration: none;
  color: rgba(180, 200, 230, 0.7);
  transition: color 0.3s ease;
}

.beian-wrapper a:last-child:hover {
  color: #60a5fa;
}

/* ========== 响应式 ========== */
@media (max-width: 480px) {
  .login-box {
    padding: 32px 24px;
  }
  
  .login-box h2 {
    font-size: 20px;
  }
  
  .title-desc {
    font-size: 12px;
    margin-bottom: 28px;
  }
  
  .form-item .form-input,
  .form-item .form-select {
    height: 44px;
    font-size: 14px;
    padding: 0 14px;
  }
  
  .login-btn {
    height: 46px;
    font-size: 14px;
  }
  
  .copyright {
    font-size: 11px;
    margin-top: 28px;
    padding-top: 16px;
  }

  .beian-wrapper {
    flex-wrap: wrap;
    gap: 4px 0;
  }

  .beian-separator {
    width: 4px;
  }
}
</style>